import React, { useState, useEffect } from 'react';
import { api } from '@/App';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { computeToolStats, computeDeviation } from '@/utils/toolLifeCalculations';

const statusColors = {
  Draft: 'bg-slate-100 text-slate-700',
  Submitted: 'bg-blue-100 text-blue-700',
  Approved: 'bg-green-100 text-green-700',
  Rejected: 'bg-red-100 text-red-700',
};

/** Phase life for row ci = pieces from this row to the next (same as machine card). */
function checkpointLife(checkpoints, ci) {
  if (ci < 0 || !checkpoints.length || ci >= checkpoints.length - 1) return null;
  return Math.abs((checkpoints[ci + 1].qty ?? 0) - (checkpoints[ci].qty ?? 0));
}

function getStandardRange(standards, partId, toolCode, insertType, operation) {
  if (!standards?.length || !partId || !toolCode || !insertType) return null;
  const std = standards.find(s =>
    s.part_id === partId &&
    s.tool_code === toolCode.toUpperCase() &&
    s.insert_type_code === insertType.toUpperCase()
  );
  if (!std) return null;
  if (operation === 'OTR') {
    return { min: std.frequency_cnc_ind_setup_min, max: std.frequency_cnc_ind_setup_max };
  }
  return { min: std.frequency_1st_setup_min, max: std.frequency_1st_setup_max };
}

function DeviationBadge({ deviation }) {
  if (!deviation) return <span className="text-slate-300">-</span>;
  if (deviation.status === 'ok') return <span className="text-green-600 font-medium">OK</span>;
  if (deviation.status === 'below') return <span className="text-red-600 font-medium">{deviation.value}</span>;
  return <span className="text-amber-600 font-medium">+{deviation.value}</span>;
}

function ToolDetailSection({ tool, ti, standardRange }) {
  const stats = computeToolStats(tool.checkpoints || []);
  const avgDeviation = computeDeviation(stats.avgLife, standardRange);
  return (
    <div className="border border-slate-200 rounded-lg p-3 space-y-2 bg-slate-50/50">
      <div className="font-medium text-sm">
        Tool {tool.tool_code} {tool.insert_type && '\u2014 ' + tool.insert_type}
      </div>
      {(tool.checkpoints || []).length > 0 && (
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-100/50">
              <TableHead className="text-xs">#</TableHead>
              <TableHead className="text-xs">QTY</TableHead>
              <TableHead className="text-xs">Phase</TableHead>
              <TableHead className="text-xs">Phase Life</TableHead>
              <TableHead className="text-xs">Std Range</TableHead>
              <TableHead className="text-xs">Deviation</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tool.checkpoints.map((cp, ci) => {
              const life = checkpointLife(tool.checkpoints, ci);
              const deviation = computeDeviation(life, standardRange);
              const phase = cp.insert_phase;
              const totalPhases = cp.total_phases ?? 6;
              const phaseDisplay = phase == null || phase === '' ? '—' : phase === 1 ? 'N' : `${phase}/${totalPhases}`;
              return (
                <TableRow key={ci}>
                  <TableCell className="text-xs py-1">{ci + 1}</TableCell>
                  <TableCell className="font-mono text-sm py-1">{cp.qty}</TableCell>
                  <TableCell className="text-sm py-1">{phaseDisplay}</TableCell>
                  <TableCell className="font-mono text-sm py-1">{life !== null ? life : '-'}</TableCell>
                  <TableCell className="py-1">
                    <span className="text-xs font-mono text-slate-500">
                      {standardRange ? `${standardRange.min}\u2013${standardRange.max}` : <span className="text-slate-300">N/A</span>}
                    </span>
                  </TableCell>
                  <TableCell className="py-1 text-xs font-mono">
                    <DeviationBadge deviation={deviation} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      <div className="flex gap-4 text-xs text-slate-600 pt-1 border-t border-slate-100">
        <span>Inserts Used: <strong>{stats.totalInserts}</strong></span>
        <span className="flex items-center gap-1.5">
          Avg Phase Life: <strong>{stats.avgLife !== null ? stats.avgLife.toFixed(1) : '-'}</strong> pcs
          {avgDeviation && <DeviationBadge deviation={avgDeviation} />}
        </span>
      </div>
      {standardRange && (
        <div className="text-xs text-slate-500">
          Standard: {standardRange.min}{'\u2013'}{standardRange.max} pcs
        </div>
      )}
    </div>
  );
}

export function ShiftLogDetailDialog({ log, open, onOpenChange }) {
  const [standards, setStandards] = useState([]);

  useEffect(() => {
    if (!open || !log) { setStandards([]); return; }
    api.get('/tool-life-standards').then(res => setStandards(res.data || [])).catch(() => setStandards([]));
  }, [open, log?.id]);

  if (!log) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="detail-dialog">
        <DialogHeader>
          <DialogTitle>Shift Log Details</DialogTitle>
          <DialogDescription>
            {log.machine_code} | {log.date} | {log.shift} Shift
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-slate-500">Machine:</span> <strong>{log.machine_code}</strong></div>
            <div><span className="text-slate-500">Part No:</span> <strong>{log.part_no || '-'}</strong></div>
            <div>
              <span className="text-slate-500">Operation: </span>
              <Badge variant="outline" className={log.operation === 'OBR' ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'}>{log.operation}</Badge>
            </div>
            <div>
              <span className="text-slate-500">Status: </span>
              <Badge variant="outline" className={statusColors[log.status]}>{log.status}</Badge>
            </div>
            <div><span className="text-slate-500">Shift Incharge:</span> <strong>{log.shift_incharge}</strong></div>
            <div><span className="text-slate-500">Date:</span> <strong>{log.date}</strong></div>
          </div>

          {log.rejection_reason && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm">
              <span className="font-medium text-red-700">Rejection Reason:</span> {log.rejection_reason}
            </div>
          )}

          {(log.tools || []).map((tool, ti) => (
            <ToolDetailSection
              key={ti}
              tool={tool}
              ti={ti}
              standardRange={getStandardRange(standards, log.part_no_id, tool.tool_code, tool.insert_type, log.operation)}
            />
          ))}

          {log.summary?.insert_counts?.length > 0 && (
            <div className="text-sm">
              <strong className="text-slate-700">Insert summary:</strong>
              <div className="flex gap-3 mt-1 flex-wrap">
                {log.summary.insert_counts.map((ic, i) => {
                  const used = ic.quantity_used ?? ic.quantity_issued ?? 0;
                  const issued = ic.quantity_issued ?? 0;
                  const hasIssued = typeof ic.quantity_used === 'number';
                  return (
                    <span key={i} className="font-mono">
                      {ic.insert_type_code}: {hasIssued ? `issued ${issued}, used ${used}, remaining ${Math.max(0, issued - used)}` : `used ${used}`}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
